import React, { useCallback, useMemo, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import { Line } from "react-chartjs-2";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../components/common/helpers/formatDate";
import { toFormatPrice } from "../common/helpers/function";
import DashboardDownloadMenu from "./DashboardDownloadMenu";
import { buildTimestampedPdfFilename, buildTimestampedPngFilename, downloadElementAsPdf, downloadElementAsPng } from "./downloadCapturePng";
import { buildModalLineChartLabels } from "./dashboardModalChartLabels";
import "./dashboardChartJsSetup";
import "./TotalSalesGraphModal.scss";

const COLOR_CURRENT = "#5CC7C1";
const COLOR_PREVIOUS = "#F9BC44";

/** Mock line data (0–125K) — replace with API series later */
const MOCK_CURRENT = [73000, 82000, 78000, 65000, 55000, 48000, 62000, 21000, 96000, 88000, 23000, 21000];
const MOCK_PREVIOUS = [20000, 25000, 30000, 45000, 50000, 52000, 48000, 87000, 14000, 35000, 55000, 69000];

function padSeries(series, len) {
  if (series.length >= len) return series.slice(0, len);
  const next = [...series];
  while (next.length < len) {
    next.push(next[next.length - 1] ?? 0);
  }
  return next;
}

const ModalTrendUp = ({ pct }) => (
  <span className="pos-total-sales-modal__trend pos-total-sales-modal__trend--up">
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M8 4l4 6H4l4-6z" fill="currentColor" />
    </svg>
    <span>{pct}</span>
  </span>
);

const ModalTrendDown = ({ pct }) => (
  <span className="pos-total-sales-modal__trend pos-total-sales-modal__trend--down">
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M8 12l-4-6h8l-4 6z" fill="currentColor" />
    </svg>
    <span>{pct}</span>
  </span>
);

/**
 * Total sales line chart + comparison table (graph modal).
 * @param {object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {Date | null} props.startDate
 * @param {Date | null} props.endDate
 */
const TotalSalesGraphModal = ({ open, onClose, startDate, endDate, data }) => {
  const { t } = useTranslation();
  const modalCaptureRef = useRef(null);

  const onExportPdf = useCallback(async () => {
    await downloadElementAsPdf(modalCaptureRef.current, buildTimestampedPdfFilename("total-sales"));
  }, []);

  const onExportPng = useCallback(async () => {
    await downloadElementAsPng(modalCaptureRef.current, buildTimestampedPngFilename("total-sales"));
  }, []);

  const dayCount = useMemo(() => {
    if (!startDate || !endDate) return 12;
    return (
      moment(endDate).startOf("day").diff(moment(startDate).startOf("day"), "days") + 1
    );
  }, [startDate, endDate]);

  const rangeLabel = useMemo(() => {
    if (!startDate || !endDate) {
      return `${t("datePlaceholder", { defaultValue: "DD/MM/YYYY" })} — ${t("datePlaceholder", { defaultValue: "DD/MM/YYYY" })}`;
    }
    return `${formatDate(startDate)} — ${formatDate(endDate)}`;
  }, [startDate, endDate, t]);

  const currentHeaderDates = useMemo(() => {
    if (data?.currentPeriod) return `(${data.currentPeriod})`;
    if (!startDate || !endDate) return "(1/9/2024 - 12/9/2024)";
    return `(${formatDate(startDate)} - ${formatDate(endDate)})`;
  }, [data?.currentPeriod, startDate, endDate]);

  const previousRangeLabel = useMemo(() => {
    if (data?.previousPeriod) return `(${data.previousPeriod})`;
    if (!startDate || !endDate) return "(20/8/2024 - 31/8/2024)";
    const pe = moment(startDate).subtract(1, "day").startOf("day");
    const ps = moment(pe).subtract(dayCount - 1, "day");
    return `(${formatDate(ps.toDate())} - ${formatDate(pe.toDate())})`;
  }, [data?.previousPeriod, startDate, endDate, dayCount]);

  const chartLabels = useMemo(() => {
    const fromApi = Array.isArray(data?.points) ? data.points.map((p) => String(p?.x ?? "")) : [];
    return fromApi.length ? fromApi : buildModalLineChartLabels(startDate, endDate);
  }, [data?.points, startDate, endDate]);

  const chartData = useMemo(() => {
    const len = chartLabels.length;
    const fromApiCur = Array.isArray(data?.points) ? data.points.map((p) => Number(p?.current) || 0) : [];
    const fromApiPrev = Array.isArray(data?.points) ? data.points.map((p) => Number(p?.previous) || 0) : [];
    const cur = fromApiCur.length ? padSeries(fromApiCur, len) : padSeries(MOCK_CURRENT, len);
    const prev = fromApiPrev.length ? padSeries(fromApiPrev, len) : padSeries(MOCK_PREVIOUS, len);
    return {
      labels: chartLabels,
      datasets: [
        {
          label: t("Current", { defaultValue: "Current" }),
          data: cur,
          borderColor: COLOR_CURRENT,
          backgroundColor: "transparent",
          borderWidth: 2,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
        {
          label: t("Previous period", { defaultValue: "Previous period" }),
          data: prev,
          borderColor: COLOR_PREVIOUS,
          backgroundColor: "transparent",
          borderWidth: 2,
          tension: 0,
          pointRadius: 0,
          pointHoverRadius: 4,
        },
      ],
    };
  }, [chartLabels, data?.points, t]);

  const maxY = useMemo(() => {
    const all = [...(chartData?.datasets?.[0]?.data ?? []), ...(chartData?.datasets?.[1]?.data ?? [])];
    const peak = Math.max(0, ...all);
    return peak <= 0 ? 1000 : Math.ceil(peak * 1.2);
  }, [chartData]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      elements: {
        line: {
          borderJoinStyle: "miter",
          borderCapStyle: "butt",
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            title: (items) => {
              const item = items[0];
              if (!item) return [];
              const chart = item.chart;
              const labels = chart?.data?.labels ?? [];
              const raw = labels[item.dataIndex];
              const text = raw != null ? String(raw) : "";
              return text ? [`${text}:`] : [];
            },
            label: (ctx) => {
              const v = ctx.parsed?.y;
              if (v == null) return "";
              const k = v >= 1000 ? `${(v / 1000).toFixed(v % 1000 ? 1 : 0)}K` : String(v);
              return `${ctx.dataset.label}: ${k}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            font: { size: 11, family: "Segoe UI, sans-serif" },
            color: "#666666",
          },
        },
        y: {
          min: 0,
          max: maxY,
          border: { display: false },
          grid: { color: "#E6E6E6" },
          ticks: {
            stepSize: Math.max(1, Math.ceil(maxY / 5)),
            font: { size: 11, family: "Segoe UI, sans-serif" },
            color: "#666666",
            callback: (value) => {
              if (value === 0) return "0";
              return `${value / 1000}K`;
            },
          },
        },
      },
    }),
    [maxY]
  );

  const headlineAmount = useMemo(() => {
    const direct = Number(data?.mainCurrent);
    const rows = Array.isArray(data?.table) ? data.table : [];
    const norm = (s) =>
      String(s ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
    const totalSaleRow =
      rows.find((r) => norm(r?.label) === "total sale") ?? rows.find((r) => norm(r?.label) === "total");
    const fromTable = Number(totalSaleRow?.current);

    if (Number.isFinite(fromTable) && (direct === 0 || Number.isNaN(direct))) return fromTable;
    if (Number.isFinite(direct) && direct !== 0) return direct;
    if (Number.isFinite(fromTable)) return fromTable;
    if (Number.isFinite(direct)) return direct;
    if (data == null || data.mainCurrent === undefined || data.mainCurrent === null) return 1000000;
    return 0;
  }, [data]);

  const mainAmount = toFormatPrice(headlineAmount, { addSymbol: true });
  const trendPct = `${Number(data?.trendPercent ?? 8.5).toFixed(2)}%`;
  const trendUp = Boolean(data?.trendUp ?? true);
  const trendDiff = Number(data?.diff ?? 78341);
  const rows = Array.isArray(data?.table) && data.table.length ? data.table : [
    { label: t("Gross sale", { defaultValue: "Gross sale" }), current: 1111111, previous: 1024066 },
    { label: t("Discount", { defaultValue: "Discount" }), current: 111111, previous: 162645 },
    { label: t("Total sale", { defaultValue: "Total sale" }), current: 1000000, previous: 921659 },
  ];
  const dayHint =
    dayCount === 1
      ? `(1 ${t("Day", { defaultValue: "Day" })})`
      : `(${dayCount} ${t("Days", { defaultValue: "Days" })})`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      className="pos-total-sales-modal"
      aria-labelledby="pos-total-sales-modal-title"
      slotProps={{
        paper: {
          className: "pos-total-sales-modal__paper",
          elevation: 8,
        },
      }}
    >
      <div className="pos-total-sales-modal__inner" ref={modalCaptureRef}>
        <header className="pos-total-sales-modal__header">
          <h2 className="pos-total-sales-modal__title" id="pos-total-sales-modal-title">
            {t("Total Sales", { defaultValue: "Total Sales" })}
          </h2>
          <button type="button" className="pos-total-sales-modal__close" onClick={onClose} aria-label={t("Close", { defaultValue: "Close" })}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M18 6L6 18M6 6l12 12" stroke="#A4A4A4" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div className="pos-total-sales-modal__content">
          <div className="pos-total-sales-modal__summary-row">
            <div className="pos-total-sales-modal__summary-left">
              <p className="pos-total-sales-modal__big-value">{mainAmount}</p>
              <div className="pos-total-sales-modal__summary-meta">
                <span className="pos-total-sales-modal__vs">{t("vsPreviousPeriod", { defaultValue: "Vs previous period" })}</span>
                {trendUp ? <ModalTrendUp pct={trendPct} /> : <ModalTrendDown pct={trendPct} />}
                <span className="pos-total-sales-modal__delta">{trendDiff >= 0 ? `+${toFormatPrice(trendDiff)}` : `-${toFormatPrice(Math.abs(trendDiff))}`}</span>
                <span className="pos-total-sales-modal__period-tag">
                  {dayCount}{" "}
                  {dayCount === 1
                    ? t("Day", { defaultValue: "Day" })
                    : t("Days", { defaultValue: "Days" })}
                </span>
              </div>
            </div>
            <div className="pos-total-sales-modal__summary-actions">
              <DashboardDownloadMenu
                buttonClassName="pos-total-sales-modal__icon-btn"
                onDownloadPdf={onExportPdf}
                onDownloadPng={onExportPng}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M12 3v12m0 0l4-4m-4 4l-4-4M5 21h14"
                    stroke="#606060"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </DashboardDownloadMenu>
              <div className="pos-total-sales-modal__range-pill" role="group" aria-label={t("Date range", { defaultValue: "Date range" })}>
                <span>{rangeLabel}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <rect x="3" y="5" width="18" height="16" rx="2" stroke="#606060" strokeWidth="1.2" />
                  <path d="M8 3v4M16 3v4M3 11h18" stroke="#606060" strokeWidth="1.2" />
                </svg>
              </div>
              <span className="pos-total-sales-modal__day-hint">{dayHint}</span>
            </div>
          </div>

          <div className="pos-total-sales-modal__chart-wrap">
            <div className="pos-total-sales-modal__chart-canvas">
              <Line data={chartData} options={chartOptions} />
            </div>
            <ul className="pos-total-sales-modal__chart-legend" aria-label={t("Chart legend", { defaultValue: "Chart legend" })}>
              <li className="pos-total-sales-modal__chart-legend-item">
                <span
                  className="pos-total-sales-modal__chart-legend-dot pos-total-sales-modal__chart-legend-dot--current"
                  aria-hidden
                />
                <span>{t("Current", { defaultValue: "Current" })}</span>
              </li>
              <li className="pos-total-sales-modal__chart-legend-item">
                <span
                  className="pos-total-sales-modal__chart-legend-dot pos-total-sales-modal__chart-legend-dot--previous"
                  aria-hidden
                />
                <span>{t("Previous period", { defaultValue: "Previous period" })}</span>
              </li>
            </ul>
          </div>

          <div className="pos-total-sales-modal__table-wrap">
            <div className="pos-total-sales-modal__thead">
              <div className="pos-total-sales-modal__th pos-total-sales-modal__th--label" />
              <div className="pos-total-sales-modal__th pos-total-sales-modal__th--current">
                <span className="pos-total-sales-modal__th-title">{t("Current", { defaultValue: "Current" })}</span>
                <span className="pos-total-sales-modal__th-sub">{currentHeaderDates}</span>
              </div>
              <div className="pos-total-sales-modal__th pos-total-sales-modal__th--previous">
                <span className="pos-total-sales-modal__th-title">{t("Previous period", { defaultValue: "Previous period" })}</span>
                <span className="pos-total-sales-modal__th-sub">{previousRangeLabel}</span>
              </div>
            </div>
            <div className="pos-total-sales-modal__tbody">
              {rows.map((row, idx) => {
                const current = Number(row?.current) || 0;
                const previous = Number(row?.previous) || 0;
                const diff = current - previous;
                const pct = previous === 0 ? 0 : Math.abs((diff / previous) * 100);
                return (
                  <div className="pos-total-sales-modal__tr" key={`sale-row-${idx}-${row?.label ?? idx}`}>
                    <div className="pos-total-sales-modal__td pos-total-sales-modal__td--label">{row?.label}</div>
                    <div className="pos-total-sales-modal__td">
                      <span className="pos-total-sales-modal__num">{toFormatPrice(current)}</span>
                      {diff >= 0 ? <ModalTrendUp pct={`${pct.toFixed(2)}%`} /> : <ModalTrendDown pct={`${pct.toFixed(2)}%`} />}
                      <span className="pos-total-sales-modal__delta pos-total-sales-modal__delta--inline">
                        {diff >= 0 ? `+${toFormatPrice(diff)}` : `-${toFormatPrice(Math.abs(diff))}`}
                      </span>
                    </div>
                    <div className="pos-total-sales-modal__td pos-total-sales-modal__td--plain">{toFormatPrice(previous)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default TotalSalesGraphModal;
